import { useState, useCallback, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useLanguage } from "../contexts/LanguageContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { toast } from "sonner";
import { safeExternalHref } from "../lib/utils";
import { Eye, EyeOff, ArrowLeft, Send, Shield, AlertCircle } from "lucide-react";
import api from "../lib/api";

// Dynamic Logo component
const DynamicLogo = () => {
  const { getLogo } = useTheme();
  const logo = getLogo();
  return (
    <div className="w-10 h-10 relative">
      <div className="absolute inset-0 bg-gradient-to-br from-primary to-secondary rounded-full" />
      <div 
        className="relative w-full h-full flex items-center justify-center text-black p-1.5 rounded-full overflow-hidden"
        dangerouslySetInnerHTML={{ __html: logo.svg }}
      />
    </div>
  );
};

const RegisterPage = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [recaptchaEnabled, setRecaptchaEnabled] = useState(false);
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState("");
  const recaptchaRef = useRef(null);
  const { setUser } = useAuth();
  const { siteContent } = useTheme();
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("public/settings").then((r) => {
      setRecaptchaEnabled(!!r.data?.recaptcha_enabled);
      setRecaptchaSiteKey((r.data?.recaptcha_site_key || "").trim());
    }).catch(() => {});
  }, []);

  const handleRecaptchaChange = useCallback((token) => {
    setRecaptchaToken(token || "");
  }, []);

  const handleRecaptchaExpired = useCallback(() => {
    setRecaptchaToken("");
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error(language === "tr" ? "Şifreler eşleşmiyor" : "Passwords don't match");
      return;
    }
    
    if (password.length < 6) {
      toast.error(language === "tr" ? "Şifre en az 6 karakter olmalı" : "Password must be at least 6 characters");
      return;
    }

    if (recaptchaEnabled && !recaptchaToken) {
      toast.error(language === "tr" ? "Lütfen 'Ben robot değilim' kutusunu işaretleyin." : "Please complete the 'I\'m not a robot' checkbox.");
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await api.post("/auth/register", {
        name,
        email,
        password,
        ...(recaptchaEnabled && recaptchaToken ? { captcha_token: recaptchaToken } : {})
      });
      
      const { token, user: userData } = response.data;
      localStorage.setItem("token", token);
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      
      setUser(userData);
      
      toast.success(language === "tr" ? "Kayıt başarılı!" : "Registration successful!");
      navigate("/dashboard");
    } catch (error) {
      const errorMessage = error.response?.data?.detail || (language === "tr" ? "Kayıt başarısız" : "Registration failed");
      toast.error(errorMessage);
      if (recaptchaEnabled && error.response?.status === 400 && recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken("");
      }
    } finally {
      setLoading(false);
    }
  }, [name, email, password, confirmPassword, recaptchaToken, recaptchaEnabled, setUser, language, navigate]);

  return (
    <div className="min-h-screen grid-texture flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors font-mono text-sm">
            <ArrowLeft className="w-4 h-4" />
            {t("nav.home")}
          </Link>
          <LanguageSwitcher />
        </div>

        <div className="glass p-8">
          <div className="flex items-center gap-2 mb-8">
            <DynamicLogo />
            <div className="sparkle-wrapper">
              <span className="sparkle"></span>
              <span className="sparkle"></span>
              <span className="sparkle"></span>
              <span className="sparkle"></span>
              <span className="glow-credit font-mono font-bold text-xl tracking-normal">ROOT-SEO</span>
            </div>
          </div>

          <h1 className="text-2xl font-mono font-bold mb-2 uppercase">{t("auth.register.title")}</h1>
          <p className="text-muted-foreground mb-6">{t("auth.register.subtitle")}</p>
          
          {/* Email Notice */}
          <div className="flex items-start gap-2 p-3 mb-6 bg-blue-500/10 border border-blue-500/30 rounded text-sm">
            <AlertCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
            <span className="text-blue-200">
              {language === "tr" 
                ? "Kayıt için Gmail, Outlook, Yahoo gibi güvenilir e-posta sağlayıcıları kullanın."
                : "Use trusted email providers like Gmail, Outlook, Yahoo for registration."}
            </span>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="font-mono text-sm uppercase tracking-wider">{t("auth.name")}</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={language === "tr" ? "Ad Soyad" : "Full Name"}
                required
                className="bg-black/50 border-white/10 focus:border-primary/50 rounded-none font-mono h-12"
                data-testid="register-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="font-mono text-sm uppercase tracking-wider">{t("auth.email")}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@gmail.com"
                required
                className="bg-black/50 border-white/10 focus:border-primary/50 rounded-none font-mono h-12"
                data-testid="register-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="font-mono text-sm uppercase tracking-wider">{t("auth.password")}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="bg-black/50 border-white/10 focus:border-primary/50 rounded-none font-mono h-12 pr-10"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="font-mono text-sm uppercase tracking-wider">{t("auth.confirmPassword")}</Label>
              <Input
                id="confirmPassword"
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="bg-black/50 border-white/10 focus:border-primary/50 rounded-none font-mono h-12"
                data-testid="register-confirm-password-input"
              />
            </div>

            {recaptchaEnabled && recaptchaSiteKey && (
              <div className="flex flex-col items-center gap-2">
                <div className="recaptcha-wrapper flex justify-center [&_.grecaptcha-badge]:!bottom-6 [&_.grecaptcha-badge]:!right-6">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={recaptchaSiteKey}
                    onChange={handleRecaptchaChange}
                    onExpired={handleRecaptchaExpired}
                    theme="dark"
                  />
                </div>
                {!recaptchaToken && (
                  <p className="text-xs text-amber-400/90 font-mono">
                    {language === "tr" ? "Kayıt olmak için yukarıdaki kutucuğu işaretlemeniz gerekir." : "You must complete the checkbox above to register."}
                  </p>
                )}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || (recaptchaEnabled && !recaptchaToken)}
              className="w-full font-mono uppercase tracking-wider bg-primary text-black hover:bg-primary/90 rounded-none h-12 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="register-submit-btn"
            >
              {loading ? (language === "tr" ? "Kayıt yapılıyor..." : "Registering...") : t("auth.register.button")}
            </Button>
          </form>
          
          {recaptchaEnabled && recaptchaSiteKey && (
            <div className="flex items-center justify-center gap-2 mt-4 text-xs text-muted-foreground">
              <Shield className="w-3 h-3" />
              <span>{language === "tr" ? "reCAPTCHA v2 ile korunuyor" : "Protected by reCAPTCHA v2"}</span>
            </div>
          )}

          <p className="text-center mt-6 text-muted-foreground">
            {t("auth.register.hasAccount")}{" "}
            <Link to="/login" className="text-primary hover:underline font-mono" data-testid="go-to-login">
              {t("nav.login")}
            </Link>
          </p>
          
          {/* Telegram Link */}
          {safeExternalHref(siteContent?.footer?.telegramLink) && (
            <div className="text-center mt-4">
              <a 
                href={safeExternalHref(siteContent.footer.telegramLink)} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-mono text-sm transition-colors"
              >
                <Send className="w-4 h-4" />
                {language === "tr" ? "Telegram Kanalımız" : "Our Telegram Channel"}
              </a>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="text-center mt-6 space-y-2">
          <p className="text-xs text-muted-foreground font-mono">© ROOT-SEO {t("footer.rights")}</p>
          <div className="sparkle-wrapper inline-block">
            <span className="sparkle"></span>
            <span className="sparkle"></span>
            <span className="sparkle"></span>
            <span className="sparkle"></span>
            <span className="sparkle"></span>
            <span className="sparkle"></span>
            <span className="glow-credit text-xs font-mono font-bold">
              Moderated by AnubisROOT-NoxShell
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
